import { create } from "zustand";
import { devtools } from "zustand/middleware";
import * as UserApi from "@src/api/UserApi";
import * as AuthApi from "@src/api/AuthApi";
import * as DistributorApi from "@src/api/DistributorApi";
import { Distributor } from "@src/constants/Distributors";
import {
  DistributorConnectionStatus,
  DistributorConnectionStatuses,
} from "@src/components/widgets/AccountDistributorSettings/DistributorConnectionConstants";
import { DistributorTermsOfService } from "@src/types/termsOfService";
import { DistributorApiHealthStatus } from "@src/constants/DistributorApiHealthStatuses";
import { initializePendo } from "@src/utils/pendo";
import { Clinic, ClinicUser } from "@src/types/clinic";

export type RolePermission = {
  role: string;
  permission: string;
  level: string;
};

export type User = {
  id: string;
  fullName: string;
  activeClinic?: Clinic;
  clinicUsers: ClinicUser[];
  role: string;
  permissions: RolePermission[];
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  demoMode: boolean;
};

export type DistributorConnection = {
  distributor: Distributor;
  status: DistributorConnectionStatus;
  isPreferred: boolean;
  credentialId: string;
  amountSpentLastMonth: number;
  amountSpentYearToDate: number;
  insideSalesContactName: string;
  insideSalesContactPhone: string;
  insideSalesContactEmail: string;
  outsideSalesContactName: string;
  outsideSalesContactPhone: string;
  outsideSalesContactEmail: string;
  connectToDistributorLink: string;
  currentTerms: DistributorTermsOfService | null;
  userAgreedToTerms: boolean;
  needsTermsUpdate?: boolean;
};

export type CurrentDistributorApiHealthStatus = {
  distributor: Distributor;
  status: DistributorApiHealthStatus;
};

type UserState = {
  currentUser: User | null;
  distributorConnections: DistributorConnection[] | null;
  distributorApiHealthStatuses: CurrentDistributorApiHealthStatus[] | null;
  loading: boolean;
  loadingDistributorConnections: boolean;
  loadingDistributorApiHealthStatuses: boolean;
  settingActiveClinic: boolean;
  error: string | null;
  fetchUser: () => Promise<void>;
  fetchDistributorConnections: (options?: {
    includeOAuthLink?: boolean;
  }) => Promise<void>;
  getDistributorConnection: (
    distributorSlug: Distributor | string,
  ) => DistributorConnection | undefined;
  isDistributorConnected: (distributorSlug: Distributor | string) => boolean;
  updateDistributorConnectionStatus: (
    distributor: Distributor,
    status: DistributorConnectionStatus,
  ) => void;
  updateDistributorPreferredStatus: (
    distributor: Distributor,
    isPreferred: boolean,
  ) => void;
  fetchDistributorApiHealthStatuses: () => Promise<void>;
  setActiveClinic: (clinicId: string, onSuccess?: () => void) => void;
};

export const useCurrentUserStore = create<UserState>()(
  devtools((set, get) => {
    const store: UserState = {
      currentUser: null,
      distributorConnections: null,
      distributorApiHealthStatuses: null,
      loading: false,
      loadingDistributorConnections: false,
      loadingDistributorApiHealthStatuses: false,
      error: null,
      fetchUser: async () => {
        set({ loading: true, error: null }, false, "currentUser/fetch-start");
        try {
          const response = await UserApi.getCurrentUser();
          const user = response.body;
          set(
            { currentUser: user, loading: false },
            false,
            "currentUser/fetch-success",
          );
          // Initialize Pendo after user is successfully fetched and authenticated
          if (user) {
            initializePendo(user);
          }
        } catch (err: any) {
          set(
            { error: err.message || "Unknown error", loading: false },
            false,
            "currentUser/fetch-error",
          );
        }
      },
      fetchDistributorConnections: async (options?: {
        includeOAuthLink?: boolean;
      }) => {
        set(
          { loadingDistributorConnections: true },
          false,
          "distributorConnections/fetch-start",
        );
        try {
          const response =
            await AuthApi.getAccountDistributorConnections(options);
          set(
            {
              distributorConnections: response.body,
              loadingDistributorConnections: false,
            },
            false,
            "distributorConnections/fetch-success",
          );
        } catch (err: any) {
          console.error("Error fetching distributor connections:", err);
          set(
            {
              distributorConnections: null,
              loadingDistributorConnections: false,
            },
            false,
            "distributorConnections/fetch-error",
          );
        }
      },
      getDistributorConnection: (distributorSlug: Distributor | string) => {
        const { distributorConnections } = get();
        return distributorConnections?.find(
          (conn) => conn.distributor === distributorSlug,
        );
      },
      isDistributorConnected: (distributorSlug: Distributor | string) => {
        const { distributorConnections } = get();
        const connection = distributorConnections?.find(
          (conn) => conn.distributor === distributorSlug,
        );
        return (
          connection?.status === DistributorConnectionStatuses.Connected ||
          false
        );
      },
      updateDistributorConnectionStatus: (
        distributor: Distributor,
        status: DistributorConnectionStatus,
      ) => {
        const { distributorConnections } = get();
        if (!distributorConnections) return;

        const updated = distributorConnections.map((conn) =>
          conn.distributor === distributor ? { ...conn, status } : conn,
        );
        set(
          { distributorConnections: updated },
          false,
          "distributorConnections/update-status",
        );
      },
      updateDistributorPreferredStatus: (
        distributor: Distributor,
        isPreferred: boolean,
      ) => {
        const { distributorConnections } = get();
        if (!distributorConnections) return;

        const updated = distributorConnections.map((conn) =>
          conn.distributor === distributor ? { ...conn, isPreferred } : conn,
        );
        set(
          { distributorConnections: updated },
          false,
          "distributorConnections/update-preferred",
        );
      },
      fetchDistributorApiHealthStatuses: async () => {
        set(
          { loadingDistributorApiHealthStatuses: true },
          false,
          "distributorApiHealthStatuses/fetch-start",
        );
        try {
          const response =
            await DistributorApi.getDistributorApiHealthStatuses();
          set(
            {
              distributorApiHealthStatuses: response.body,
              loadingDistributorApiHealthStatuses: false,
            },
            false,
            "distributorApiHealthStatuses/fetch-success",
          );
        } catch (err: any) {
          console.error("Error fetching distributor API health statuses:", err);
          set(
            {
              distributorApiHealthStatuses: null,
              loadingDistributorApiHealthStatuses: false,
            },
            false,
            "distributorApiHealthStatuses/fetch-error",
          );
        }
      },
      setActiveClinic: async (clinicId: string, onSuccess: () => void) => {
        set({ settingActiveClinic: true }, false, "activeClinic/set-start");
        try {
          const response = await UserApi.setCurrentUserActiveClinic(clinicId);
          set(
            {
              currentUser: response.body,
              settingActiveClinic: false,
            },
            false,
            "activeClinic/set-success",
          );

          if (onSuccess) {
            onSuccess();
          }
        } catch (err: any) {
          console.error("Error fetching distributor API health statuses:", err);
          set(
            {
              settingActiveClinic: false,
            },
            false,
            "activeClinic/set-error",
          );
        }
      },
    };

    store.fetchUser();
    store.fetchDistributorConnections();

    return store;
  }),
);
